<template>
  <div class="container">
    <div class="mb-3 d-flex justify-space-between align-center">
      <span class="title">Erros de Backup S3 de Terapias</span>
      <v-btn color="info" @click="loadStats">
        <v-icon class="mr-2">mdi-chart-bar</v-icon>
        Estatísticas
      </v-btn>
    </div>

    <!-- Estatísticas -->
    <v-card class="mb-4" v-if="stats">
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-chart-line</v-icon>
        Resumo (Últimos {{ statsDays }} dias)
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 primary--text">{{ stats.totalErrors }}</div>
                <div class="caption">Total de Erros</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 error--text">{{ stats.networkErrors }}</div>
                <div class="caption">Erros de Rede</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 warning--text">{{ stats.s3Errors }}</div>
                <div class="caption">Erros S3</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 info--text">{{ stats.avgRetries.toFixed(1) }}</div>
                <div class="caption">Média de Retries</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Top Erros -->
        <v-row class="mt-2" v-if="stats.errorsByType && stats.errorsByType.length > 0">
          <v-col cols="12" md="4">
            <div class="subtitle-2 mb-2">Top Tipos de Erro</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th class="text-right">Qtd</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in stats.errorsByType.slice(0, 5)" :key="index">
                    <td>{{ item.errorType }}</td>
                    <td class="text-right">{{ item.count }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>

          <v-col cols="12" md="4" v-if="stats.errorsByBrowser && stats.errorsByBrowser.length > 0">
            <div class="subtitle-2 mb-2">Por Browser</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Browser</th>
                    <th class="text-right">Qtd</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in stats.errorsByBrowser" :key="index">
                    <td>{{ item.browser }}</td>
                    <td class="text-right">{{ item.count }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>

          <v-col cols="12" md="4" v-if="stats.errorsByOs && stats.errorsByOs.length > 0">
            <div class="subtitle-2 mb-2">Por Sistema Operacional</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>OS</th>
                    <th class="text-right">Qtd</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in stats.errorsByOs" :key="index">
                    <td>{{ item.os }}</td>
                    <td class="text-right">{{ item.count }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Filtros -->
    <filters-panel>
      <v-form ref="filters">
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.draftId"
              filled
              label="Draft ID"
              type="number"
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.errorType"
              filled
              label="Tipo de Erro"
              :items="errorTypes"
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.isNetworkError"
              filled
              label="Erro de Rede"
              :items="[
                { text: 'Todos', value: undefined },
                { text: 'Sim', value: true },
                { text: 'Não', value: false },
              ]"
              hide-details
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.browser"
              filled
              label="Browser"
              :items="browsers"
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.os"
              filled
              label="OS"
              :items="operatingSystems"
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.startDate"
              filled
              label="Data Início"
              type="date"
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.endDate"
              filled
              label="Data Fim"
              type="date"
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model.number="filters.limit"
              filled
              label="Itens por página"
              type="number"
              hide-details
              :min="10"
              :max="100"
            />
          </v-col>
        </v-row>
      </v-form>
      <v-row justify="end" class="mt-2">
        <v-btn color="secondary" class="mr-2" @click="clearFilters">
          <v-icon left>mdi-filter-remove</v-icon>
          Limpar
        </v-btn>
        <v-btn color="primary" @click="list">
          <v-icon left>mdi-filter</v-icon>
          Filtrar
        </v-btn>
      </v-row>
    </filters-panel>

    <v-divider class="my-3"></v-divider>

    <!-- Tabela -->
    <v-data-table
      :items="errors.data"
      :headers="headers"
      :server-items-length="errors.total"
      :options.sync="options"
      :loading="loading"
      class="elevation-1"
      @update:options="handleOptionsUpdate"
    >
      <template v-slot:item.errorType="{ item }">
        <v-chip small color="error" outlined>
          {{ item.errorType }}
        </v-chip>
      </template>

      <template v-slot:item.isNetworkError="{ item }">
        <v-icon :color="item.isNetworkError ? 'error' : 'info'" small>
          {{ item.isNetworkError ? "mdi-wifi-off" : "mdi-cloud-alert" }}
        </v-icon>
      </template>

      <template v-slot:item.httpStatus="{ item }">
        <v-chip
          v-if="item.httpStatus"
          small
          :color="getStatusColor(item.httpStatus)"
        >
          {{ item.httpStatus }}
        </v-chip>
        <span v-else class="grey--text">-</span>
      </template>

      <template v-slot:item.browser="{ item }">
        <span v-if="item.browser">{{ item.browser }}</span>
        <span v-else class="grey--text">-</span>
      </template>

      <template v-slot:item.os="{ item }">
        <span v-if="item.os">{{ item.os }}</span>
        <span v-else class="grey--text">-</span>
      </template>

      <template v-slot:item.payloadSize="{ item }">
        <span v-if="item.payloadSize">{{ formatBytes(item.payloadSize) }}</span>
        <span v-else class="grey--text">-</span>
      </template>

      <template v-slot:item.errorTimestamp="{ item }">
        {{ formatDate(item.errorTimestamp) }}
      </template>

      <template v-slot:item.actions="{ item }">
        <table-action-button
          text="Detalhes"
          @click="viewDetails(item)"
          icon="mdi-eye"
        />
        <table-action-button
          text="Excluir"
          @click="confirmDelete(item)"
          icon="mdi-delete"
          color="error"
        />
      </template>
    </v-data-table>

    <!-- Dialog de Detalhes -->
    <error-details-dialog ref="detailsDialog" />
  </div>
</template>

<script>
import {
  findErrors,
  findErrorById,
  getErrorStats,
  deleteError,
} from "@/services/therapy-errors-service";
import ErrorDetailsDialog from "@/components/TherapyErrors/ErrorDetailsDialog";
import moment from "moment";

export default {
  name: "TherapyErrors",
  components: {
    "error-details-dialog": ErrorDetailsDialog,
  },
  data: () => ({
    errors: {
      data: [],
      total: 0,
      page: 1,
      limit: 50,
    },
    stats: null,
    statsDays: 7,
    loading: false,
    filters: {
      draftId: null,
      errorType: null,
      isNetworkError: undefined,
      browser: null,
      os: null,
      startDate: null,
      endDate: null,
      limit: 50,
    },
    options: {},
    headers: [
      { text: "ID", value: "id", width: 80 },
      { text: "Draft ID", value: "draftId", width: 100 },
      { text: "Tipo", value: "errorType", width: 150 },
      { text: "Rede", value: "isNetworkError", width: 80 },
      { text: "Status HTTP", value: "httpStatus", width: 120 },
      { text: "Browser", value: "browser", width: 100 },
      { text: "OS", value: "os", width: 100 },
      { text: "Tamanho", value: "payloadSize", width: 100 },
      { text: "Retries", value: "retryCount", width: 80 },
      { text: "Data/Hora", value: "errorTimestamp", width: 150 },
      {
        text: "Ações",
        value: "actions",
        sortable: false,
        width: 150,
        align: "center",
      },
    ],
    errorTypes: [
      "NetworkError",
      "TimeoutError",
      "PresignedUrlExpired",
      "S3Error",
      "UnknownError",
    ],
    browsers: ["Chrome", "Firefox", "Safari", "Edge", "Opera"],
    operatingSystems: ["Windows", "MacOS", "Linux", "Android", "iOS"],
  }),
  mounted() {
    this.list();
    this.loadStats();
  },
  methods: {
    async list() {
      try {
        this.loading = true;
        const response = await findErrors({
          ...this.filters,
          page: this.errors.page,
        });
        this.errors = response;
      } catch (error) {
        console.error("Erro ao listar erros:", error);
        this.$toasted.error("Erro ao carregar lista de erros");
      } finally {
        this.loading = false;
      }
    },
    async loadStats() {
      try {
        this.stats = await getErrorStats(this.statsDays);
      } catch (error) {
        console.error("Erro ao carregar estatísticas:", error);
        this.$toasted.error("Erro ao carregar estatísticas");
      }
    },
    async viewDetails(item) {
      try {
        const error = await findErrorById(item.id);
        this.$refs.detailsDialog.open(error);
      } catch (error) {
        console.error("Erro ao buscar detalhes:", error);
        this.$toasted.error("Erro ao carregar detalhes do erro");
      }
    },
    async confirmDelete(item) {
      const confirm = await this.$swal({
        title: "Excluir erro?",
        text: `Deseja realmente excluir o erro #${item.id}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#FF5252",
      });

      if (confirm.isConfirmed) {
        await this.delete(item.id);
      }
    },
    async delete(errorId) {
      try {
        await deleteError(errorId);
        this.$toasted.success("Erro excluído com sucesso");
        this.list();
        this.loadStats();
      } catch (error) {
        console.error("Erro ao excluir:", error);
        this.$toasted.error("Erro ao excluir registro");
      }
    },
    clearFilters() {
      this.filters = {
        draftId: null,
        errorType: null,
        isNetworkError: undefined,
        browser: null,
        os: null,
        startDate: null,
        endDate: null,
        limit: 50,
      };
      this.list();
    },
    handleOptionsUpdate(options) {
      if (options.page !== this.errors.page) {
        this.errors.page = options.page;
        this.list();
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return moment(dateString).format('DD/MM/YYYY HH:mm');
    },
    formatBytes(bytes) {
      if (bytes === 0 || !bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },
    getStatusColor(status) {
      if (status >= 500) return "error";
      if (status >= 400) return "warning";
      if (status >= 200 && status < 300) return "success";
      return "grey";
    },
  },
};
</script>

<style scoped>
.container {
  padding: 20px;
}
</style>
